<template>
  <div>
    <!--
      Plano de cobrança do estabelecimento: qual régua de taxa governa as vendas
      dele. O admin atribui planos aqui; o estabelecimento vê a mesma informação
      (somente leitura) — a edição da régua em si mora em Financeiro > Planos de
      Cobrança, admin-only.
    -->
    <vx-card class="mb-4">
      <div class="flex justify-between items-center mb-3">
        <div>
          <h4 class="mb-0">{{ $t('fee_plans.current_plan') }}</h4>
          <small class="text-muted">{{ $t('fee_plans.current_plan_hint') }}</small>
        </div>
        <vs-chip v-if="current && current.is_default" color="primary" class="text-base">
          {{ $t('fee_plans.default') }}
        </vs-chip>
      </div>

      <div v-if="current && current.plan">
        <div class="mb-2">
          <span class="font-semibold">{{ current.plan.name }}</span>
          <small v-if="current.active_version" class="text-muted ml-2">
            v{{ current.active_version.version }}
            · {{ $t('fee_plans.effective_since', { date: $utils.format.date(current.active_version.effective_from) }) }}
          </small>
        </div>

        <!-- Régua vigente, em tabela legível -->
        <table v-if="current.active_version" class="w-full mb-2" style="font-size: .9rem">
          <thead>
            <tr class="text-left text-muted">
              <th class="pb-1">{{ $t('fee_plans.scope') }}</th>
              <th class="pb-1">{{ $t('fee_plans.rate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in current.active_version.rules" :key="rule.id" class="border-top">
              <td class="py-1">{{ ruleScopeLabel(rule) }}</td>
              <td class="py-1">
                <template v-if="rule.pricing_mode === 'FLAT'">
                  {{ rateLabel(rule.fee_percent, rule.fee_fixed_cents) }}
                </template>
                <template v-else>
                  <div v-for="tier in rule.tiers" :key="tier.id">
                    <small>
                      {{ tierThresholdLabel(tier) }} → {{ rateLabel(tier.fee_percent, tier.fee_fixed_cents) }}
                    </small>
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
        <b-alert v-else show variant="warning" class="p-1">
          <small>{{ $t('fee_plans.no_active_version') }}</small>
        </b-alert>
      </div>
      <b-alert v-else-if="loaded" show variant="warning" class="p-1">
        <small>{{ $t('fee_plans.no_plan') }}</small>
      </b-alert>
    </vx-card>

    <!-- Atribuição (admin) -->
    <vx-card v-if="canAssign" class="mb-4">
      <h4 class="mb-3">{{ $t('fee_plans.assign_title') }}</h4>
      <div class="vx-row items-end">
        <div class="vx-col sm:w-5/12 w-full">
          <b-form-group :label="$t('fee_plans.plan')" label-size="sm">
            <b-form-select v-model="assignment.plan_id" :options="planOptions" size="sm"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('fee_plans.effective_from')" label-size="sm">
            <b-form-input v-model="assignment.effective_from" type="date" size="sm"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full pb-1">
          <b-button variant="primary" size="sm" :disabled="!assignment.plan_id" @click="assign">
            {{ $t('fee_plans.assign') }}
          </b-button>
        </div>
      </div>
      <small class="text-muted">{{ $t('fee_plans.assign_hint') }}</small>
    </vx-card>

    <!-- Histórico -->
    <vx-card v-if="current && current.history && current.history.length">
      <h4 class="mb-3">{{ $t('fee_plans.history') }}</h4>
      <table class="w-full" style="font-size: .9rem">
        <thead>
          <tr class="text-left text-muted">
            <th class="pb-1">{{ $t('fee_plans.plan') }}</th>
            <th class="pb-1">{{ $t('fee_plans.effective_from') }}</th>
            <th class="pb-1">{{ $t('fee_plans.notes') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in current.history" :key="item.id" class="border-top">
            <td class="py-1">{{ item.plan ? item.plan.name : item.plan_id }}</td>
            <td class="py-1">{{ $utils.format.date(item.effective_from) }}</td>
            <td class="py-1"><small class="text-muted">{{ item.notes }}</small></td>
          </tr>
        </tbody>
      </table>
    </vx-card>
  </div>
</template>

<script>
import { BAlert, BButton, BFormGroup, BFormInput, BFormSelect } from 'bootstrap-vue'
import { currencyFormatter } from '@/util/Util'
import LegalEntityFeePlanService from '@/services/api/LegalEntityFeePlanService'
import FeePlanService from '@/services/api/FeePlanService'

const PRODUCT_TYPE_LABELS = { LES: 'Aulas', RSP: 'Espaços', SPL: 'Planos de assinatura' }

export default {
  components: { BAlert, BButton, BFormGroup, BFormInput, BFormSelect },

  props: {
    legalEntityId: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      service: null,
      planService: null,
      loaded: false,
      current: null,
      planOptions: [],
      assignment: {
        plan_id: null,
        effective_from: null
      }
    }
  },

  computed: {
    canAssign() {
      return this.$acl.hasPermission('fee_plans.assign')
    }
  },

  methods: {
    ruleScopeLabel(rule) {
      switch (rule.scope_type) {
        case 'BASE': return this.$t('fee_plans.scope_.BASE')
        case 'PRODUCT_TYPE': return PRODUCT_TYPE_LABELS[rule.product_type] || rule.product_type
        case 'MODALITY': return rule.modality ? rule.modality.name : this.$t('fee_plans.scope_.MODALITY')
        case 'PRODUCT': return rule.product ? rule.product.name : this.$t('fee_plans.scope_.PRODUCT')
        default: return rule.scope_type
      }
    },
    rateLabel(percent, fixedCents) {
      const parts = []
      if (Number(percent)) parts.push(`${Number(percent)}%`)
      if (fixedCents) parts.push(currencyFormatter(fixedCents / 100))
      return parts.length ? parts.join(' + ') : this.$t('fee_plans.rate_zero')
    },
    tierThresholdLabel(tier) {
      const conditions = []
      if (tier.min_gmv_cents) conditions.push(`≥ ${currencyFormatter(tier.min_gmv_cents / 100)}/mês`)
      if (tier.min_sales_count) conditions.push(`≥ ${tier.min_sales_count} vendas/mês`)
      return conditions.length ? conditions.join(' e ') : this.$t('fee_plans.tier_floor')
    },
    loadCurrent() {
      if (!this.legalEntityId) return
      this.$vs.loading()
      this.service.currentPlan(this.legalEntityId)
        .then(data => {
          this.current = data
          this.loaded = true
        })
        .catch(error => this.showErrors(null, error, this))
        .finally(() => this.$vs.loading.close())
    },
    loadPlans() {
      if (!this.canAssign) return
      this.planService.grid('/grid', { page: 1, per_page: 100 }).then(response => {
        this.planOptions = this._.get(response, 'model.data', []).map(plan => ({
          value: plan.id,
          text: plan.is_default ? `${plan.name} (${this.$t('fee_plans.default')})` : plan.name
        }))
      }).catch(() => {})
    },
    assign() {
      this.$vs.loading()
      this.planService.assign(this.assignment.plan_id, {
        legal_entity_id: this.legalEntityId,
        effective_from: this.assignment.effective_from || undefined
      })
        .then(() => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.assignment.plan_id = null
          this.loadCurrent()
        })
        .catch(error => this.showErrors(null, error, this))
        .finally(() => this.$vs.loading.close())
    }
  },

  beforeMount() {
    this.service = LegalEntityFeePlanService.build(this.$vs)
    this.planService = FeePlanService.build(this.$vs)
  },

  mounted() {
    this.loadCurrent()
    this.loadPlans()
  }
}
</script>
